import { useCallback, useMemo } from 'react';
import TaskList from '@tiptap/extension-task-list';
import ListKeymap from '@tiptap/extension-list-keymap';
import UniqueID from '@tiptap-pro/extension-unique-id';

import { Extensions } from '@tiptap/core';
import getClientConfig from '../../../utils/getClientConfig';
import { generateTaskId } from '../../../../common/tiptap/extensions/taskList/taskListItemIdUtils';
import { TaskListDocument } from '../../../../common/tiptap/extensions/taskList/TaskListDocument';
import { TaskListTitle } from '../../../../common/tiptap/extensions/taskList/TaskListTitle';
import { TaskListItem } from '../../../../common/tiptap/extensions/taskList/TaskListItem';
import type {
    TaskItemAssignmentShortcutContext,
    TaskItemDueDateShortcutContext,
} from '../../../../common/tiptap/extensions/taskList/taskListItemKeyboardHandlers';
import isSingleEmptyTaskListEditor from '../../../../common/tiptap/extensions/taskList/isSingleEmptyTaskListEditor';
import TaskListDeleteIcon from './TaskListDeleteIcon';
import TaskItemChipsContainer from '../../chipsPanel/TaskItemChipsContainer';
import { selectElementChip } from '../../chipsPanel/elementChipsActions';
import {
    buildTaskItemAssignmentChipDescriptor,
    buildTaskItemAssignmentPlaceholderChipDescriptor,
    buildTaskItemDueDateChipDescriptor,
} from '../../chipsPanel/taskItemChipsUtils';
import TaskListDragHandle from './TaskListDragHandle';
import TaskItemActivityIndicator from '../../activity/TaskItemActivityIndicator';
import type { PlaceholderOptions } from '@tiptap/extension-placeholder';
import useClientCommonTiptapEditorExtensions, {
    UseClientCommonTiptapEditorExtensionsArgs,
} from '../../../components/tiptapEditor/useClientCommonTiptapEditorExtensions';
import { InlineComment } from '../../../../common/tiptap/extensions/inlineComment/InlineComment';
import useInlineCommentOptions from '../../attachedComment/useInlineCommentOptions';
import { usePlaySound } from '../../../sounds/usePlaySound';
import { SoundId } from '../../../sounds/soundConstants';
import { useTranslator } from '../../../components/i18n/useTranslator';
import useUpdatedRef from '../../../utils/react/useUpdatedRef';
import {
    DispatchFetchInlineBoardLinkDetails,
    FetchInlineBoardLinkDetailsResponse,
} from '../../../../common/tiptap/extensions/inlineBoardLink/inlineBoardLinkConstants';
import { fetchInlineBoardLinkDetails } from '../../../components/tiptapEditor/elementTiptapActions';
import { InlineBoardLink } from '../../../../common/tiptap/extensions/inlineBoardLink/InlineBoardLink';
import InlineBoardLinkResolvedContent from '../../card/tiptap/InlineBoardLinkResolvedContent';
import {
    NoteInsertionSource,
    NoteInsertionType,
    trackNoteInsertionEvent,
} from '../../../analytics/noteInsertionAnalyticsUtils';
import { useSelector } from 'react-redux';
import { selectIsMobileAppMode } from '../../../platform/platformSelector';

type TaskListPlaceholderFn = Exclude<PlaceholderOptions['placeholder'], string>;

const { urlBase } = getClientConfig();

/**
 * Gets the node-aware placeholder text: the title node and task item nodes show different prompts.
 * Provided to the common Placeholder extension via `placeholderOptions`.
 *
 * The translator is read through a ref because the Placeholder extension captures its options when
 * the editor is created, so a closed-over translator would keep serving the language in use back
 * then. Reading the ref means the next decoration pass after a language change is translated.
 */
const useTaskListPlaceholder = (): TaskListPlaceholderFn => {
    const translate = useTranslator('elements', 'elementTypes.taskList');
    const translateRef = useUpdatedRef(translate);

    return useCallback<TaskListPlaceholderFn>(
        ({ node }) =>
            node.type.name === 'taskListTitle'
                ? translateRef.current('title.placeholder')
                : translateRef.current('emptyPlaceholder'),
        [translateRef],
    );
};

type UseTaskListEditorExtensionsArgs = UseClientCommonTiptapEditorExtensionsArgs & {
    // Whether the task items can be modified — chips edited, items dragged out (from the board's write permission).
    canModifyContent: boolean;
    // The owning canvas element whose single-selection gates task-item chip activation.
    elementId: string;
    // Removes the whole task list element, for the deletions that leave nothing behind.
    // Omitted where the element can't be trashed (e.g. Storybook), which makes those deletions no-op.
    moveTaskListToTrash?: () => void;
    trackInsertions?: boolean;
};

/**
 * Gets the extensions for the standalone task list editor.
 *
 * Composes Milanote's shared client/common Tiptap extensions (which supply the StarterKit
 * nodes, marks, Placeholder, and the ExternalStoreSynchroniser that persists content) with
 * the task-list-specific document/title/list/item nodes.
 */
const useTaskListEditorExtensions = ({
    saveContent,
    isEditable,
    dispatch,
    onHighlightsChanged,
    onEmptyBackspace,
    canModifyContent,
    elementId,
    moveTaskListToTrash,
    trackInsertions = false,
}: UseTaskListEditorExtensionsArgs): Extensions => {
    const playSound = usePlaySound();

    const isMobileAppMode = useSelector(selectIsMobileAppMode);
    const onTaskCheckedChange = useCallback(
        ({ checked }: { checked: boolean }) => playSound(checked ? SoundId.CheckboxTick : SoundId.CheckboxUntick),
        [playSound],
    );

    const onTaskCreated = useCallback(() => playSound(SoundId.TaskNewTaskAppear), [playSound]);

    const onDeleteTask = useCallback(() => playSound(SoundId.DeleteSomething), [playSound]);

    // Deleting the last task of an untitled list leaves nothing to show, so the element goes too
    const onDeleteTaskList = useCallback(() => {
        if (!moveTaskListToTrash) return;

        playSound(SoundId.DeleteSomething);
        moveTaskListToTrash();
    }, [moveTaskListToTrash, playSound]);

    // Opens the assignee picker when the assignment shortcut is pressed in a task
    // item, by selecting the task's assignment chip (the shortcut handler has
    // already ensured `showAssignments`, so the chip renders).
    const onAssignmentShortcut = useCallback(
        ({ taskId, assignments }: TaskItemAssignmentShortcutContext) => {
            const containerId = taskId ?? '';

            // A task item has a single assignee slot, so only the first assignment can have a chip.
            const [assignment] = assignments;
            const assignmentChip = assignment ? buildTaskItemAssignmentChipDescriptor(taskId, assignment) : undefined;

            // Nobody assigned - or an assignment missing a field the chip renders - opens the placeholder instead
            // Both descriptors carry the same chip id, so the picker opens on the same chip either way
            const chip = assignmentChip ?? buildTaskItemAssignmentPlaceholderChipDescriptor(taskId);

            dispatch?.(selectElementChip({ elementId, containerId, chip }));
        },
        [dispatch, elementId],
    );

    // Opens the due-date picker when the due-date shortcut is pressed in a task
    // item, by selecting the task's due-date chip (the shortcut handler has
    // already ensured `showDueDate`, so the chip renders).
    const onDueDateShortcut = useCallback(
        ({ taskId }: TaskItemDueDateShortcutContext) => {
            const containerId = taskId ?? '';

            dispatch?.(
                selectElementChip({
                    elementId,
                    containerId,
                    chip: buildTaskItemDueDateChipDescriptor({ taskId }),
                }),
            );
        },
        [dispatch, elementId],
    );

    const dispatchFetchInlineBoardLinkDetails = useCallback<DispatchFetchInlineBoardLinkDetails>(
        (elementId, permissionId) =>
            dispatch?.(fetchInlineBoardLinkDetails(elementId, permissionId)) as FetchInlineBoardLinkDetailsResponse,
        [dispatch],
    );

    // Inline comment threads are attached inline to the task list element, so a mark
    // anywhere in the list (title or task item) is backed by a child of `elementId`.
    const inlineCommentOptions = useInlineCommentOptions({ elementId, isEditable });

    const placeholder = useTaskListPlaceholder();

    const placeholderOptions = useMemo(
        () => ({
            placeholder,
            showOnlyWhenEditable: false,
            showOnlyCurrent: false,
            includeChildren: true,
        }),
        [placeholder],
    );

    const commonExtensions = useClientCommonTiptapEditorExtensions({
        saveContent,
        isEditable,
        dispatch,
        onHighlightsChanged,
        onEmptyBackspace,
        isEmptyDeleteTarget: isSingleEmptyTaskListEditor,
        placeholderOptions,
    });

    return useMemo(
        () => [
            TaskListDocument,
            TaskListTitle,
            TaskList,
            // Supplies a stable per-task `taskId` attr used to key chip selection/editing.
            // Mirrors the card editor's use of UniqueID for stable node keys.
            UniqueID.configure({
                attributeName: 'taskId',
                types: ['taskItem'],
                generateID: generateTaskId,
            }),
            InlineBoardLink.configure({
                dispatchFetchInlineBoardLinkDetails,
                urlOrigin: urlBase,
                NodeViewResolvedContent: InlineBoardLinkResolvedContent,
                addSpaceOnCreation: isMobileAppMode,
                onInsertedFromPaste: trackInsertions
                    ? () => trackNoteInsertionEvent(NoteInsertionType.BOARD_LINK, NoteInsertionSource.PASTE)
                    : undefined,
            }),
            // Later extensions win keymap precedence, so ListKeymap must come first
            ListKeymap,
            // TaskListItem keymap will take precedence
            TaskListItem.configure({
                DeleteIconComponent: TaskListDeleteIcon,
                DragHandleComponent: TaskListDragHandle,
                ChipsComponent: TaskItemChipsContainer,
                ActivityIndicatorComponent: TaskItemActivityIndicator,
                canModifyContent,
                elementId,
                nested: true,
                onAssignmentShortcut,
                onDueDateShortcut,
                onTaskCheckedChange,
                onTaskCreated,
                onDeleteTask,
                onDeleteTaskList,
            }),
            InlineComment.configure(inlineCommentOptions),
            ...commonExtensions,
        ],
        [
            canModifyContent,
            commonExtensions,
            dispatchFetchInlineBoardLinkDetails,
            isMobileAppMode,
            trackInsertions,
            elementId,
            inlineCommentOptions,
            onAssignmentShortcut,
            onDueDateShortcut,
            onTaskCheckedChange,
            onTaskCreated,
            onDeleteTask,
            onDeleteTaskList,
        ],
    );
};

export default useTaskListEditorExtensions;
